import triangleLoader from '@/assets/branding/triangles-loader.gif';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFetchAccount } from '@/features/account/api/get-account';
import useUpdateLeverage from '@/features/account/api/update-leverage';
import { CustomSlider } from '@/features/trade/order/components';
import { getMaxLeverage } from '@/features/trade/order/utils/math';
import useFundedAccount, {
  getCapitalMaxLeverage,
} from '@/hooks/use-funded-account';
import useSymbol from '@/hooks/use-symbol';
import { cn } from '@/lib/utils';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import { useModalStore } from '@/store/use-modal-store';
import { CapitalStatus } from '@/types';
import { abbrFromSym } from '@/utils/token-symbol';
import { FormEvent, useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

const getLeverageRange = (maxLeverage: number) => {
  let range: number[];
  if (maxLeverage >= 100) {
    range = [20, 40, 60, 80, 100];
  } else if (maxLeverage >= 50) {
    range = [10, 20, 30, 40, 50];
  } else if (maxLeverage >= 20) {
    range = [1, 5, 10, 15, 20];
  } else if (maxLeverage >= 10) {
    range = [1, 2, 5, 8, 10];
  } else {
    range = [1, 2, 3, 4, 5];
  }
  if (range[range.length - 1] !== maxLeverage) {
    range[range.length - 1] = maxLeverage;
  }
  return range;
};

export default function LeverageModal() {
  const isOpen = useModalStore((state) => state.isLeverageModalOpen);
  const symbol = useSymbol();

  const { initMarginRatio, leverage: marketLeverage } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      initMarginRatio: state.marketSpec.initMarginRatio,
      leverage: state.leverage ?? 1,
    })),
  );

  const { isLoading: isAccountLoading } = useFetchAccount();

  const { isEval, isFundedOrEval } = useFundedAccount();

  const { hasAuth, capitalStatus } = useAccountStore(
    useShallow((state) => ({
      hasAuth: state.hasAuth,
      capitalStatus: state.getCapitalStatus(state.address),
    })),
  );

  const hasPosition = useCurrentAccount((state) => symbol in state.positions);

  const capitalMaxLeverage = getCapitalMaxLeverage(symbol);
  const maxLeverageDecimal = getMaxLeverage(initMarginRatio)?.decimal;
  const maxLeverage = maxLeverageDecimal
    ? isFundedOrEval
      ? Math.min(Number(maxLeverageDecimal), capitalMaxLeverage)
      : Number(maxLeverageDecimal)
    : 0;

  const currentLeverage = Math.min(maxLeverage, marketLeverage);

  const [leverage, setLeverage] = useState(currentLeverage);

  const { mutateAsync: updateLeverage, isPending } = useUpdateLeverage({
    onSuccess: (_lvg) => {
      useModalStore.setState({ isLeverageModalOpen: false });
    },
  });

  useEffect(() => {
    setLeverage(currentLeverage);
  }, [currentLeverage]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    updateLeverage({
      symbol,
      value: leverage,
      options: { waitForConfirm: true },
    });
  }

  const isSmallerThanCurrent =
    !!currentLeverage && hasPosition && leverage < currentLeverage;

  const isDisabled = (() => {
    if (isPending || isSmallerThanCurrent) return true;

    return currentLeverage === leverage;
  })();

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) =>
        useModalStore.setState({ isLeverageModalOpen: open })
      }
    >
      <DialogTrigger
        disabled={
          !hasAuth ||
          !currentLeverage ||
          (isEval && capitalStatus !== CapitalStatus.ACTIVE)
        }
        className={cn(
          'flex h-full w-max flex-col items-center justify-center border-l border-border bg-background px-6 text-primary transition-colors disabled:cursor-not-allowed sm:px-8 md:ml-auto',
        )}
      >
        <span className="hidden sm:inline-block">Change Leverage</span>
        <span className="font-mono text-lg font-bold tracking-widest">
          {isAccountLoading ? (
            <img
              style={{ height: 21 }}
              className="mx-auto"
              src={triangleLoader}
              alt=""
            />
          ) : !hasAuth ? (
            '-'
          ) : currentLeverage ? (
            currentLeverage + 'X'
          ) : (
            '-'
          )}
        </span>
      </DialogTrigger>
      <DialogContent
        className="gap-0 border border-border bg-background p-0 sm:max-w-md sm:rounded-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <form onSubmit={onSubmit}>
          <DialogHeader className="px-5 pt-6">
            <DialogTitle className="text-xl text-foreground">
              Change Leverage
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 px-5 py-4">
            <p className="text-vestgrey-200">
              Control the leverage used for {abbrFromSym(symbol)} positions.{' '}
              {isFundedOrEval
                ? `Funded accounts are limited to ${capitalMaxLeverage}X
                  leverage.`
                : `The maximum leverage is ${maxLeverage}X.`}
            </p>
            <CustomSlider
              onChange={(val) => setLeverage(val)}
              value={leverage}
              minValue={1}
              maxValue={maxLeverage}
              unitSymbol="X"
              buttonValues={getLeverageRange(maxLeverage)}
              step={1}
            />
          </div>
          <DialogFooter className="mt-6">
            {isSmallerThanCurrent ? (
              <TooltipProvider>
                <Tooltip
                  open={!isSmallerThanCurrent ? false : undefined}
                  delayDuration={0}
                >
                  <TooltipTrigger className="w-full">
                    <div className="w-full">
                      <Button
                        disabled
                        className="h-16 w-full rounded-none py-6 font-mono text-lg font-normal uppercase tracking-wider"
                      >
                        Invalid Leverage
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Cannot lower leverage with a position open
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button
                disabled={isDisabled}
                className="h-16 w-full rounded-none py-6 font-mono text-lg font-normal uppercase tracking-wider"
              >
                Set
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
